import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { DocumentData } from '../employee-overview/employee-overview.domain';
import { EnvironmentService } from './environment.service';
import { NetzTranslateService } from './netz-translate.service';
import { ErrorService } from './error/error.service';
import { OrderProcessExportFilter } from '../jurisdiction-overview/jurisdiction-overview.domain';
import { UrlSegments } from '../jurisdiction/jurisdiction.constants';

@Injectable({
  providedIn: 'root',
})
export class FileService {
  baseUrl: string;
  private allowedFileTypes: string[] = ['application/pdf', 'text/plain', 'image/jpg', 'image/jpeg', 'image/png', 'image/gif'];
  private shoudCheckForUploadFileTypeAndSize: boolean = this.environmentService.shoudCheckForUploadFileTypeAndSize;

  constructor(
    private httpClient: HttpClient,
    private environmentService: EnvironmentService,
    private translate: NetzTranslateService,
    private errorService: ErrorService
  ) {
    this.baseUrl = this.environmentService.apiUrl;
  }

  uploadDocuments(event: any, taId: string, name: string): Observable<any> {
    if (!this.isFileTypeAndSizeAllowed(event.currentFiles[0])) {
      return throwError('error');
    }
    const formData = new FormData();
    formData.append('document', event.files[0]);
    formData.append('ta_id', taId);
    formData.append('name', name);
    return this.httpClient.post(this.baseUrl + 'document', formData);
  }

  checkForAvailableDocuments(documents: DocumentData[], name: string): boolean {
    if (documents && documents.length) {
      const currentDocumentIndex = documents.findIndex(item => item.name === name);
      return currentDocumentIndex !== -1;
    }
    return false;
  }

  public checkForEnabledAvailableDocuments(documents: DocumentData[], name: string): boolean {
    if (documents && documents.length) {
      const currentDocumentIndex = documents.findIndex(item => item.name === name);
      return currentDocumentIndex === -1;
    }
    return true;
  }

  checkIndex(documents: DocumentData[], documentId: string): number {
    if (documents && documents.length) {
      return documents.findIndex(item => item.document_id === documentId);
    }
    return -1;
  }

  readDocument(documentId: string): Observable<any> {
    return this.httpClient.get(this.baseUrl + 'document?document_id=' + documentId, { responseType: 'blob' });
  }

  removeFile(documentId: string): Observable<any> {
    return this.httpClient.delete(this.baseUrl + 'document?document_id=' + documentId);
  }

  previewDocument(response: Blob, contentType: string, isFilefromUploader?: boolean): void {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64: string = isFilefromUploader ? reader.result.toString().split(',')[1] : atob(reader.result.toString().split(',')[1]);
      const bytes = this.base64ToArrayBuffer(base64);
      const applicationType = contentType ?? 'application/pdf';
      const blob = new Blob([bytes], { type: applicationType });
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '', 'width=1000,height=1000'); //.print();
    };
    reader.readAsDataURL(response);
  }

  base64ToArrayBuffer(base64) {
    var binaryString = window.atob(base64);
    var binaryLen = binaryString.length;
    var bytes = new Uint8Array(binaryLen);
    for (var i = 0; i < binaryLen; i++) {
      var ascii = binaryString.charCodeAt(i);
      bytes[i] = ascii;
    }
    return bytes;
  }

  downloadDocument(response: Blob, fileName: string, isFilefromUploader?: boolean): void {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base: string = isFilefromUploader ? reader.result.toString().split(',')[1] : atob(reader.result.toString().split(',')[1]);
      const downloadLink = document.createElement('a');
      downloadLink.href = 'data:application/octet-stream;base64,' + base;
      downloadLink.download = fileName;
      downloadLink.click();
    };
    reader.readAsDataURL(response);
  }

  downloadZipDocument(response: HttpResponse<Blob>): void {
    const data = response.body;
    const blob = new Blob([data], { type: 'application/zip' });
    const fileName = this.getFileNameFromHeader(response.headers);

    const a = document.createElement('a');
    document.body.appendChild(a);
    const url = window.URL.createObjectURL(blob);
    a.href = url;
    a.download = fileName;
    a.click();
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    }, 0);
  }

  getDimensionsByFilter(documents: any, name: string): DocumentData[] {
    if (documents && documents.length) {
      return documents.filter(x => x.name === name);
    } else {
      return null;
    }
  }

  uploadDocumentsForWorkflows(event: any, applicationId: string, documentType: string, name: string): Observable<any> {
    if (!this.isFileTypeAndSizeAllowed(event.currentFiles[0])) {
      return throwError('error');
    }
    const formData = new FormData();
    formData.append('document', event.currentFiles[0]);
    formData.append('application_id', applicationId);
    formData.append('name', name);
    formData.append('sk_document_type', documentType);
    return this.httpClient.post(this.baseUrl + 'application/document', formData);
  }

  uploadDocumentsForWorkflows2(file: any, applicationId: string, documentType: string, name: string): Observable<any> {
    if (!this.isFileTypeAndSizeAllowed(file)) {
      return throwError('error');
    }
    const formData = new FormData();
    formData.append('document', file);
    formData.append('application_id', applicationId);
    formData.append('name', name);
    formData.append('sk_document_type', documentType);
    return this.httpClient.post(this.baseUrl + 'application/document', formData);
  }

  uploadSingleDocument(file: File, applicationId: string, documentType: string, name: string): Observable<any> {
    if (!this.isFileTypeAndSizeAllowed(file)) {
      return throwError('error');
    }
    const formData = new FormData();
    formData.append('document', file);
    formData.append('application_id', applicationId);
    formData.append('name', name);
    formData.append('sk_document_type', documentType);
    return this.httpClient.post(this.baseUrl + 'application/document', formData);
  }

  readWorkflowDocument(documentId: string): Observable<any> {
    return this.httpClient.get(this.baseUrl + 'application/document?document_id=' + documentId, { responseType: 'blob' });
  }

  getAllDocumentsByApplicationId(
    applicationId: string,
    document?: any,
    documentType?: string,
    name?: string
  ): Observable<HttpResponse<Blob>> {
    const formData = new FormData();
    if (document) {
      formData.append('document', document);
      formData.append('application_id', applicationId);
      formData.append('name', name);
      formData.append('sk_document_type', documentType);
    }

    return this.httpClient.post<Blob>(this.baseUrl + 'application/download?application_id=' + applicationId, formData, {
      responseType: 'blob' as 'json',
      observe: 'response',
    });
  }

  removeWorkflowDocument(documentId: string, options?: { skipHistoryLog?: boolean }): Observable<any> {
    let newLocal = this.baseUrl + 'application/document?document_id=' + documentId;
    if (options?.skipHistoryLog) {
      newLocal += '&skipHistoryLog=true';
    }

    return this.httpClient.delete(newLocal);
  }

  modifyDocumentForWorkflow(event: any, documentId: string): Observable<any> {
    if (!this.isFileTypeAndSizeAllowed(event.currentFiles[0])) {
      return throwError('error');
    }
    const formData = new FormData();
    formData.append('document', event.currentFiles[0]);
    return this.httpClient.put(this.baseUrl + 'application/document?document_id=' + documentId, formData);
  }

  modifyDocumentForWorkflow2(file: any, documentId: string): Observable<any> {
    if (!this.isFileTypeAndSizeAllowed(file)) {
      return throwError('error');
    }
    const formData = new FormData();
    formData.append('document', file);
    return this.httpClient.put(this.baseUrl + 'application/document?document_id=' + documentId, formData);
  }

  uploadDocumentsForMessages(event: any, applicationId: string, documentType: string, name: string): Observable<any> {
    if (!this.isFileTypeAndSizeAllowed(event.currentFiles[0])) {
      return throwError('error');
    }
    const formData = new FormData();
    formData.append('document', event.currentFiles[0]);
    formData.append('application_id', applicationId);
    formData.append('name', name);
    formData.append('sk_document_type', documentType);
    return this.httpClient.post(this.baseUrl + 'application/document', formData);
  }

  getFileNameFromHeader(headers: HttpHeaders): string {
    const contentDisposition = headers.get('Content-Disposition') || '';
    const matches = /filename=([^;]+)/gi.exec(contentDisposition);
    return (matches[1] || 'file.zip').trim();
  }

  downloadFileFromMessage(event) {
    if (event.target.nodeName.toLowerCase() === 'a') {
      event.preventDefault();
      const href = event.target.href;
      const id = href.match(/\d+/) || [];
      const fileName = event.target.innerText;
      if (id && id.length) {
        this.readWorkflowDocument(id[0]).subscribe((response: Blob) => {
          this.downloadDocument(response, fileName);
        });
      }
    }
  }

  isFileTypeAndSizeAllowed(file: any): boolean {
    let errorMessages = [];
    if (!this.shoudCheckForUploadFileTypeAndSize) {
      return true;
    }
    const fileTooLarge = file.size / (1024 * 1024) > 5;
    const fileTypeNotAllowed = !this.allowedFileTypes.includes(file.type);

    if (fileTooLarge) {
      errorMessages.push(this.translate.translateString('grid-comp.file-too-large-error'));
    }
    if (fileTypeNotAllowed) {
      errorMessages.push(this.translate.translateString('grid-comp.file-not-allowed-error'));
    }
    if (errorMessages.length) {
      this.errorService.createMultipleMessages(errorMessages, 'error');
      return false;
    } else {
      return true;
    }
  }

  getOrderProcessFile(filter: OrderProcessExportFilter): Observable<HttpResponse<Blob>> {
    return this.httpClient.post<Blob>(this.baseUrl + `${UrlSegments.OrderProcessExport}`, filter, {
      responseType: 'blob' as 'json',
      observe: 'response',
    });
  }
}
